// Budget demo · engine — pure reducer over Workspace.
//
// Lifecycle events drive the budget FSM (lifecycle.ts). Sources are read-only
// in the demo — the imported/uploaded records are seeded, not ingested live.
// New-spend lines can be added by hand to any budget year.

import { buildNewSpendLine, withIncrease } from "./forecast";
import { BudgetEvent, transition } from "./lifecycle";
import { BudgetLineId, CategoryId, Confidence, Workspace } from "./types";

export type WorkspaceEvent =
    | { type: "AdvanceLifecycle"; year: number; event: BudgetEvent }
    | { type: "AddNewSpend"; year: number }
    | {
          type: "EditNewSpend";
          year: number;
          lineId: BudgetLineId;
          name: string;
          categoryId: CategoryId;
          /** Quarter the spend is expected to close — e.g. "Q3 2027". */
          quarter: string;
          amount: number;
          confidence: Confidence;
      }
    | { type: "DeleteNewSpend"; year: number; lineId: BudgetLineId }
    | {
          type: "SetIncrease";
          year: number;
          lineId: BudgetLineId;
          /** FY increase as a fraction (0.09 = 9%). */
          pct: number;
      };

/** Next free `bl-manual-<year>-<n>` index — max existing + 1, so it survives
 * deletes without colliding. */
function nextManualIndex(lineIds: string[]): number {
    return (
        lineIds.reduce((max, id) => {
            const m = id.match(/^bl-manual-\d+-(\d+)$/);
            return m ? Math.max(max, Number(m[1])) : max;
        }, -1) + 1
    );
}

const replaceLines = (
    workspace: Workspace,
    year: number,
    fn: (lines: Workspace["budgets"][number]["lines"]) => Workspace["budgets"][number]["lines"]
): Workspace => ({
    ...workspace,
    budgets: workspace.budgets.map((b) =>
        b.year === year ? { ...b, lines: fn(b.lines) } : b
    ),
});

export function apply(workspace: Workspace, event: WorkspaceEvent): Workspace {
    switch (event.type) {
        case "AdvanceLifecycle": {
            const budget = workspace.budgets.find((b) => b.year === event.year);
            if (!budget) return workspace;
            const tr = transition(budget.state, event.event);
            if (!tr.ok) return workspace;
            return {
                ...workspace,
                budgets: workspace.budgets.map((b) =>
                    b.year === event.year ? { ...b, state: tr.next } : b
                ),
            };
        }

        case "AddNewSpend": {
            const budget = workspace.budgets.find((b) => b.year === event.year);
            const category = workspace.categories[0];
            if (!budget || !category) return workspace;

            const line = buildNewSpendLine(
                {
                    id: `bl-manual-${event.year}-${nextManualIndex(
                        budget.lines.map((l) => l.id)
                    )}`,
                    name: "",
                    categoryId: category.id,
                    categoryName: category.name,
                    amount: 0,
                    closeQuarter: `Q1 ${event.year}`,
                    confidence: "low",
                    owner: workspace.currentUser.name,
                    rationale: "Added manually",
                },
                event.year
            );
            return replaceLines(workspace, event.year, (lines) => [...lines, line]);
        }

        case "EditNewSpend": {
            const category = workspace.categories.find(
                (c) => c.id === event.categoryId
            );
            const existing = workspace.budgets
                .find((b) => b.year === event.year)
                ?.lines.find((l) => l.id === event.lineId);
            if (!category || !existing) return workspace;

            const line = buildNewSpendLine(
                {
                    id: event.lineId,
                    name: event.name,
                    categoryId: category.id,
                    categoryName: category.name,
                    amount: event.amount,
                    closeQuarter: event.quarter,
                    confidence: event.confidence,
                    owner: existing.owner,
                    rationale: existing.rationale,
                },
                event.year
            );
            return replaceLines(workspace, event.year, (lines) =>
                lines.map((l) => (l.id === event.lineId ? line : l))
            );
        }

        case "DeleteNewSpend":
            return replaceLines(workspace, event.year, (lines) =>
                lines.filter((l) => l.id !== event.lineId)
            );

        case "SetIncrease":
            return replaceLines(workspace, event.year, (lines) =>
                lines.map((l) =>
                    l.id === event.lineId ? withIncrease(l, event.pct) : l
                )
            );
    }
}
